import { zodResolver } from '@hookform/resolvers/zod';
import { Contact, ContactStatus, ContactType, INVITATION_TYPES } from '@wonderschool/common-base-types';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  CloseIcon,
  InfoIcon,
  showToast,
  Toast,
  ToastTypeEnum,
  Tooltip,
} from '@wonderschool/common-base-ui';
import { useFilesAccess } from '@wonderschool/file-service-client';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import PageHeader from '../../Components/Shared/PageHeader';
import { saveContact } from '../../contacts/contactsAPI';
import { useContacts } from '../../contacts/contactsHooks';
import { EnrollmentSourceEnum, EnrollmentStatusEnum } from '../../enrollments';
import StudentDocuments from '../../enrollments/components/forms/steps/StudentDocuments';
import StudentInformation from '../../enrollments/components/forms/steps/StudentInformation';
import StudentInvoice from '../../enrollments/components/forms/steps/StudentInvoice';
import StudentSummary from '../../enrollments/components/forms/steps/StudentSummary';
import { NewEnrollmentInvitationConfirmationModal } from '../../enrollments/components/modals';
import { createInvitationRecipientsFromFamily } from '../../enrollments/enrollmentsUtils';
import { InvitationRecipientType } from '../../enrollments/types';
import { dateFormatter, formatISOStringAsUtcDateOrNull, isTodayOrBefore } from '../../helpers/dates';
import { formatFullName } from '../../helpers/utils';
import { usePrimaryLocation } from '../../hooks/useLocations';
import { useNavigationPrevention } from '../../hooks/useNavigationPrevention';
import { useOrganization, useOrganizationContactsListener } from '../../hooks/useOrganizations';
import { useUser } from '../../hooks/useUser';
import { InterestedFamilyType } from '../../integrations';
import { RouteNameEnum, useRoutes } from '../../navigation';
import { newOrganizationInvoice, saveOrganizationInvoicePlan } from '../../redux/actions/invoiceActions';
import { logError } from '../../rollbar';
import {
  convertDataToInvoice,
  convertDataToPlan,
  convertDataToProrateInvoice,
} from '../../utils/enrollmentInvoiceUtils';
import { MARKETPLACE_REVENUE_SOURCE_MAP } from '../consts';
import { saveStudent } from '../studentsAPI';
import { createScheduledDaysFromInterestedFamily } from '../studentsUtils';
import CancelConfirmationDialog from './modals/CancelConfirmationModal';
import NavigationPreventionModal from './modals/NavigationPreventionModal';
import QuickSaveConfirmationModal from './modals/QuickSaveConfirmationModal';

type AddStudentPageProps = {
  onClick?: () => void;
};

const createStudentSchema = (t: (key: string) => string) =>
  z.object({
    student: z.object({
      firstName: z.string().min(1, t('common.firstNameRequired')),
      lastName: z.string().min(1, t('common.lastNameRequired')),
      gender: z.any().optional(),
      birthday: z.string().min(1, t('enrollments.errors.birthdateRequired')),
    }),
    parents: z
      .array(
        z.object({
          firstName: z.string().min(1, t('common.firstNameRequired')),
          lastName: z.string().min(1, t('common.lastNameRequired')),
          email: z.string().email(t('enrollments.errors.invalidEmail')).min(1, t('enrollments.errors.emailRequired')),
          phone: z
            .string()
            .optional()
            .refine(
              (value) => {
                if (!value) return true;
                try {
                  return parsePhoneNumber(value, { regionCode: 'US' }).valid;
                } catch {
                  return false;
                }
              },
              { message: t('common.invalidPhone') }
            ),
          contact: z.any().optional(),
          shouldSendInvitation: z.boolean().optional(),
          isInterestedFamily: z.boolean().optional(),
          hasAuthUser: z.boolean().optional(),
          uid: z.string().optional(),
          uuid: z.string().optional(),
          marketplaceFamilyId: z.number().optional(),
        })
      )
      .min(1, t('enrollments.invitationRecipientsSelectOne')),
    interestedFamily: z.any().optional(),
  });

const createEnrollmentSchema = (t: (key: string) => string) =>
  z
    .object({
      room: z.string().min(1, t('students.enrollment.validation.pleaseSelectARoom')),
      startDate: z.string().min(1, t('enrollments.enrollmentInfoStartDateRequired')),
      automaticallyEnroll: z.boolean().optional(),
      invitationRecipients: z.array(z.any()).min(1, t('enrollments.invitationRecipientsSelectOne')),
      documents: z.array(z.any()).optional(),
      scheduledDays: z.array(z.any()).min(1, t('enrollments.scheduleSelectOne')),
      parentEmail: z.string().email(t('enrollments.errors.invalidEmail')).optional(),
      billingInterval: z.string().optional(),
      billFor: z.enum(['Prior', 'Next']).optional(),
      chargeDate: z.string().optional(),
      tuition: z.number().optional(),
      recurringFees: z
        .array(
          z.object({
            type: z.string().min(1, t('enrollments.errors.feeTypeRequired')),
            category: z.string().min(1, t('enrollments.errors.feeCategoryRequired')),
            amount: z.number().min(0, t('enrollments.errors.amountPositive')),
            amountType: z.string().min(1, t('enrollments.errors.amountRequired')),
          })
        )
        .optional(),
      oneTimeFees: z
        .array(
          z.object({
            name: z.string().min(1, t('enrollments.errors.feeTypeRequired')),
            amount: z.number().min(0, t('enrollments.errors.amountPositive')),
            dueDate: z.string().min(1, t('enrollments.errors.dueDateRequired')),
          })
        )
        .optional(),
      subsidized: z.boolean().optional(),
      subsidyCopayment: z.boolean().optional(),
      tuitionAndFees: z.any().optional(),
      planStartDate: z.string().min(1, t('enrollments.enrollmentInfoStartDateRequired')),
      planEndDate: z.string().optional(),
    })
    .refine(
      (data) => {
        // If subsidized but no copayment, billing interval is not required
        if (data.subsidized && !data.subsidyCopayment) {
          return true;
        }
        // Otherwise, billing interval is required
        return !!data.billingInterval;
      },
      {
        message: t('enrollments.errors.billingIntervalRequired'),
        path: ['billingInterval'],
      }
    )
    .refine(
      (data) => {
        // If subsidized but no copayment, bill for is not required
        if (data.subsidized && !data.subsidyCopayment) {
          return true;
        }
        // Otherwise, bill for is required
        return !!data.billFor;
      },
      {
        message: t('enrollments.errors.billForRequired'),
        path: ['billFor'],
      }
    )
    .refine(
      (data) => {
        // If subsidized but no copayment, charge date is not required
        if (data.subsidized && !data.subsidyCopayment) {
          return true;
        }
        // Otherwise, charge date is required
        return !!data.chargeDate;
      },
      {
        message: t('enrollments.errors.chargeDateRequired'),
        path: ['chargeDate'],
      }
    )
    .refine(
      (data) => {
        // If subsidized but no copayment, tuition is not required
        if (data.subsidized && !data.subsidyCopayment) {
          return true;
        }
        // Otherwise, tuition is required and must be greater than 0
        return data.tuition !== undefined && data.tuition > 0;
      },
      {
        message: t('enrollments.errors.tuitionRequired'),
        path: ['tuition'],
      }
    );

type StudentFormValues = z.infer<ReturnType<typeof createStudentSchema>>;
type EnrollmentFormValues = z.infer<ReturnType<typeof createEnrollmentSchema>>;

enum Step {
  STUDENT_INFO = 1,
  DOCUMENTS = 2,
  INVOICE = 3,
  SUMMARY = 4,
}

const AddStudentPage: React.FC<AddStudentPageProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loggedInUser = useUser();
  const { gotoRouteByName } = useRoutes();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState<Step>(Step.STUDENT_INFO);
  const [interestedFamily, setInterestedFamily] = useState<InterestedFamilyType>();
  const [scheduledDays, setScheduledDays] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [extraNotes, setExtraNotes] = useState<string>('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showQuickSaveDialog, setShowQuickSaveDialog] = useState(false);
  const [contactsWithIds, setContactsWithIds] = useState<Contact[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const params: any = useParams();
  const marketplaceChildId = params.marketplaceChildId ? parseInt(params.marketplaceChildId) : undefined;

  const organization = useOrganization() as any;
  const location = usePrimaryLocation();
  const { contacts: _contacts } = useContacts();

  const roomOptions = useSelector((state: any) => state.rooms?.list || []);

  const defaultRoomId = roomOptions.length === 1 ? roomOptions[0].id : undefined;

  useOrganizationContactsListener(organization?.id);

  const studentSchema = useMemo(() => createStudentSchema(t), [t]);
  const enrollmentSchema = useMemo(() => createEnrollmentSchema(t), [t]);

  const studentForm = useForm<StudentFormValues>({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      student: {
        firstName: '',
        lastName: '',
        gender: undefined,
        birthday: '',
      },
      parents: [
        {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          shouldSendInvitation: true,
        },
      ],
    },
  });

  const enrollmentForm = useForm<EnrollmentFormValues>({
    resolver: zodResolver(enrollmentSchema),
    defaultValues: {
      room: '',
      startDate: '',
      automaticallyEnroll: true,
      invitationRecipients: [],
      documents: [],
      scheduledDays: [],
      parentEmail: '',
      billingInterval: '',
      billFor: 'Next' as const,
      chargeDate: '',
      tuition: 0,
      recurringFees: [],
      oneTimeFees: [],
      subsidized: false,
      subsidyCopayment: true,
      tuitionAndFees: {},
      planStartDate: '',
      planEndDate: '',
    },
  });

  const hasUnsavedChanges = useMemo(() => {
    const formsHaveChanges = studentForm.formState.isDirty || enrollmentForm.formState.isDirty;
    const hasScheduledDays = scheduledDays?.length > 0;
    const hasExtraNotes = extraNotes.trim().length > 0;
    const hasInterestedFamily = !!interestedFamily;

    return formsHaveChanges || hasScheduledDays || hasExtraNotes || hasInterestedFamily;
  }, [
    studentForm.formState.isDirty,
    enrollmentForm.formState.isDirty,
    scheduledDays?.length,
    extraNotes,
    interestedFamily,
  ]);

  const { modalProps } = useNavigationPrevention({
    when: !isSaving && hasUnsavedChanges && !showCancelDialog,
    pendingAction,
    setPendingAction,
  });

  useEffect(() => {
    if (interestedFamily) {
      const initialScheduledDays = createScheduledDaysFromInterestedFamily(interestedFamily);
      enrollmentForm.setValue('scheduledDays', initialScheduledDays);
      setScheduledDays(initialScheduledDays);
    } else {
      setScheduledDays([]);
    }
  }, [enrollmentForm, interestedFamily]);

  const onSaveSchedule = useCallback(
    ({ data }: any) => {
      setScheduledDays(data ?? []);
      enrollmentForm.setValue('scheduledDays', data ?? []);
    },
    [enrollmentForm]
  );

  const parents = useWatch({ control: studentForm.control, name: 'parents' }) ?? [];
  const room = useWatch({ control: enrollmentForm.control, name: 'room' }) ?? '';

  const contacts = useMemo<Contact[]>(() => {
    const contactsLocal: Contact[] = [];

    for (const parent of parents) {
      let contact;

      const status =
        parent.hasAuthUser && parent.uid
          ? ContactStatus.verified
          : parent.shouldSendInvitation
            ? ContactStatus.inviting
            : ContactStatus.new;

      if (parent.contact) {
        // Merge page-1 edits over the stored contact instead of using it verbatim.
        // When a provider picks an existing/interested-family parent, `parent.contact`
        // holds the original record while edits land on `parent.email`/`parent.phone`.
        // Returning the stored contact as-is dropped those edits, so later steps and the
        // saved family showed the old email/phone (ZD 106730). Spreading first preserves
        // identity/linkage fields (id, uid, status) so we don't mint a duplicate contact.
        contact = {
          ...parent.contact,
          firstName: parent.firstName ?? parent.contact.firstName,
          lastName: parent.lastName ?? parent.contact.lastName,
          displayName: formatFullName(parent, true),
          email: parent.email ?? parent.contact.email,
          phone: parent.phone ?? parent.contact.phone,
        };
      } else {
        contact = {
          firstName: parent.firstName,
          lastName: parent.lastName,
          displayName: formatFullName(parent, true),
          email: parent.email ?? '',
          phone: parent.phone ?? '',
          type: ContactType.FAMILY,
          relationship: 'parent',
          status,
          defaultOrganization: organization?.id ?? '',
          defaultLocation: location?.id ?? '',
          defaultRoom: room ?? '',
          organizations: organization?.id ? [organization.id] : [],
          locations: location?.id ? [location.id] : [],
          rooms: room ? [room] : [],
          allowLoginWithPin: true,
          uid: parent.uid ?? '',
          invitationType: INVITATION_TYPES.ENROLLMENT,
          invitationNotes: extraNotes,
          student: {
            // * student data for the invitation email
            // * the student hasn't been saved yet, so we need to use the form values and without the id
            displayName: formatFullName(studentForm.getValues().student, true),
            firstName: studentForm.getValues().student.firstName,
            lastName: studentForm.getValues().student.lastName,
            organization: organization?.id ?? '',
          },
        };
      }
      contactsLocal.push(contact);
    }
    return contactsLocal;
  }, [location?.id, organization?.id, parents, room, extraNotes]);

  const desiredStartDate = useMemo<string>(() => {
    if (!interestedFamily?.student?.desiredStartDate) return '';
    const date =
      dateFormatter(formatISOStringAsUtcDateOrNull(interestedFamily.student.desiredStartDate), 'YYYY-MM-DD') ?? '';
    return date;
  }, [interestedFamily]);

  const startDate = enrollmentForm.watch('startDate');
  const automaticallyEnroll = enrollmentForm.watch('automaticallyEnroll');

  const enrollmentStatus = useMemo<EnrollmentStatusEnum>(() => {
    if (!startDate) return EnrollmentStatusEnum.PENDING;
    if (automaticallyEnroll && isTodayOrBefore(startDate)) {
      return EnrollmentStatusEnum.ENROLLED;
    }
    return EnrollmentStatusEnum.PENDING;
  }, [startDate, automaticallyEnroll]);

  const enrollmentSource = useMemo<EnrollmentSourceEnum>(() => {
    return interestedFamily?.revenueSource
      ? (MARKETPLACE_REVENUE_SOURCE_MAP[interestedFamily.revenueSource] ?? EnrollmentSourceEnum.PROVIDER)
      : EnrollmentSourceEnum.PROVIDER;
  }, [interestedFamily]);

  const studentFromForm = useMemo(() => {
    const studentData = studentForm.getValues();
    const enrollmentData = enrollmentForm.getValues();

    if (!studentData.student || !studentData.parents?.length) return null;

    const family: any = {};
    const familyMembers: string[] = [];

    const updatedContacts = contactsWithIds.length > 0 ? contactsWithIds : contacts;
    updatedContacts.forEach((contact) => {
      family[contact.id!] = contact;
      familyMembers.push(contact.id!);
      if (contact.uid) familyMembers.push(contact.uid);
    });

    return {
      id: '',
      firstName: studentData.student.firstName ?? '',
      lastName: studentData.student.lastName ?? '',
      displayName: formatFullName(studentData.student, true),
      birthday: studentData.student.birthday ? new Date(studentData.student.birthday) : null,
      gender: studentData.student.gender?.value ?? '',
      defaultOrganization: organization?.id ?? '',
      defaultLocation: location?.id ?? '',
      defaultRoom: enrollmentData.room ?? '',
      organizations: organization?.id ? [organization.id] : [],
      locations: location?.id ? [location.id] : [],
      rooms: enrollmentData.room ? [enrollmentData.room] : [],
      family,
      familyMembers,
      source: enrollmentSource,
      enrollmentStatus: false,
      enrollmentDate: new Date(enrollmentData.startDate ?? '').valueOf() ?? null,
      enrollment: {
        status: enrollmentStatus,
        source: enrollmentSource,
        scheduledDays: scheduledDays,
      },
    };
  }, [
    studentForm,
    enrollmentForm,
    contactsWithIds,
    contacts,
    organization?.id,
    location?.id,
    enrollmentSource,
    enrollmentStatus,
    scheduledDays,
  ]);

  const invitationRecipients = useMemo<InvitationRecipientType[]>(() => {
    return studentFromForm ? createInvitationRecipientsFromFamily(studentFromForm) : [];
  }, [studentFromForm]);

  useEffect(() => {
    if (invitationRecipients.length > 0) {
      const firstParentEmail = invitationRecipients[0]?.email;
      if (firstParentEmail) {
        enrollmentForm.setValue('parentEmail', firstParentEmail);
      }
    }
  }, [invitationRecipients, enrollmentForm]);

  const { shareFiles } = useFilesAccess();

  const scrollToTop = useCallback(() => {
    // The nuclear option: Multiple attempts to ensure scroll position sticks
    // This accounts for any competing scroll restoration or React re-rendering

    const forceToTop = () => {
      // Target the main scrollable container (the pusher)
      const pusherContainer =
        document.querySelector('.ui.sidebar.pushable > .pusher') || document.querySelector('[class*="pusher"]');

      if (pusherContainer) {
        pusherContainer.scrollTop = 0;
      }

      // Also reset window/document scroll
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    // Immediate attempt
    forceToTop();

    // Staggered attempts to override any competing scroll events
    // These timings ensure we catch React re-renders and scroll restoration
    setTimeout(forceToTop, 10); // Catch immediate React updates
    setTimeout(forceToTop, 50); // Catch delayed state updates
    setTimeout(forceToTop, 100); // Final safety net
  }, []);

  const [documentShareFailed, setDocumentShareFailed] = useState(false);

  const shareDocuments = useCallback(
    async (savedStudentData?: any) => {
      const enrollmentData = enrollmentForm.getValues();
      const documents = enrollmentData.documents || [];
      const documentIds = documents.map((doc: any) => doc.id);

      const studentDataForSharing = savedStudentData || studentFromForm;
      if (documentIds.length > 0 && studentDataForSharing?.id) {
        try {
          await shareFiles({
            files: documentIds,
            students: [studentDataForSharing.id],
          });
        } catch (error) {
          // The grant is the only thing that lets the family read the packet, and it
          // fails often in production (CCMS-8210). This used to be fire-and-forget, so
          // the director was shown the success modal either way.
          //
          // Deliberately does not stop the submit: the student has already been created
          // and this form holds no id to save against, so leaving the director on the
          // form to retry would create a duplicate student and a duplicate invoice
          // (saveStudentLocal -> saveStudent with no id always adds). Report the failure
          // and let the flow finish; the director re-shares from the documents page.
          logError('Add student - document sharing failed', error);
          showToast(ToastTypeEnum.Error, t('enrollments.documentSharingFailed'));
          // The toast is transient and invoice creation runs after this, so it can be
          // gone before the director looks. The confirmation modal has to carry it too.
          setDocumentShareFailed(true);
        }
      }
    },
    [enrollmentForm, studentFromForm, shareFiles, t]
  );

  const saveContacts = useCallback(async () => {
    const newContacts = contacts.filter((contact) => !contact.id);
    try {
      for (const contact of newContacts) {
        const docRef = await saveContact(organization?.id, contact);
        contact.id = docRef.id;
      }

      setContactsWithIds(contacts);
      return contacts;
    } catch (error) {
      logError('Error while saving contacts', error);
      throw error;
    }
  }, [contacts, organization?.id]);

  const sendInvoices = useCallback(
    async (savedStudentData?: any) => {
      const enrollmentData = enrollmentForm.getValues();
      const tuitionAndFees = enrollmentData.tuitionAndFees;
      const oneTimeFees = enrollmentData.oneTimeFees;
      const studentDataForInvoices = savedStudentData || studentFromForm;
      if (oneTimeFees && oneTimeFees.length > 0) {
        for (const fee of oneTimeFees) {
          const invoicePayload = convertDataToInvoice(fee, loggedInUser, studentDataForInvoices);
          await dispatch(newOrganizationInvoice(organization.id, invoicePayload));
        }
      }

      if (tuitionAndFees && tuitionAndFees.tuition && Object.keys(tuitionAndFees.tuition).length > 0) {
        if (
          tuitionAndFees.tuition.detailedTuition?.subsidized &&
          !tuitionAndFees.tuition.detailedTuition?.subsidyCopayment
        ) {
          return;
        }

        const planPayload = convertDataToPlan(
          tuitionAndFees.tuition,
          loggedInUser,
          studentDataForInvoices,
          enrollmentForm.watch('planEndDate')
        );
        await dispatch(saveOrganizationInvoicePlan(organization.id, planPayload));

        if (tuitionAndFees.tuition.proratedAmount && tuitionAndFees.tuition.proratedAmount > 0) {
          const proratePayload = convertDataToProrateInvoice(
            tuitionAndFees.tuition,
            loggedInUser,
            studentDataForInvoices
          );
          await dispatch(newOrganizationInvoice(organization.id, proratePayload));
        }
      }
    },
    [enrollmentForm, studentFromForm, loggedInUser, organization.id, dispatch]
  );

  const saveStudentLocal = useCallback(async () => {
    const studentData = studentForm.getValues();
    const enrollmentData = enrollmentForm.getValues();
    const room = enrollmentData.room ?? '';

    const { room: _, ...enrollmentInformation } = enrollmentData;
    const isEnrolled = !!(
      enrollmentStatus === EnrollmentStatusEnum.ENROLLED ||
      (enrollmentInformation.automaticallyEnroll && isTodayOrBefore(enrollmentInformation.startDate))
    );

    if (!studentData.student || !studentData.parents?.length) return null;

    const updatedContacts = await saveContacts();
    const family: any = {};
    const familyMembers: string[] = [];

    updatedContacts.forEach((contact) => {
      family[contact.id!] = contact;
      familyMembers.push(contact.id!);
      if (contact.uid) familyMembers.push(contact.uid);
    });

    const studentDataToSave: any = {
      firstName: studentData.student.firstName ?? '',
      lastName: studentData.student.lastName ?? '',
      displayName: formatFullName(studentData.student, true),
      birthday: studentData.student.birthday ? new Date(studentData.student.birthday) : null,
      gender: studentData.student.gender?.value ?? '',
      defaultOrganization: organization?.id ?? '',
      defaultLocation: location?.id ?? '',
      defaultRoom: room ?? '',
      organizations: organization?.id ? [organization.id] : [],
      locations: location?.id ? [location.id] : [],
      rooms: room ? [room] : [],
      family,
      familyMembers,
      source: enrollmentSource,
      enrollmentStatus: isEnrolled,
      enrollmentDate: new Date(enrollmentInformation.startDate ?? '').valueOf() ?? null,
      enrollment: {
        ...enrollmentInformation,
        startDate: enrollmentInformation.startDate ? new Date(enrollmentInformation.startDate) : null,
        status: isEnrolled ? EnrollmentStatusEnum.ENROLLED : enrollmentStatus,
        source: enrollmentSource,
        scheduledDays: scheduledDays,
      },
    };

    if (interestedFamily) {
      let notes = interestedFamily.student?.notes ?? '';
      if (notes && interestedFamily.student?.needs) notes += ' ' + interestedFamily.student.needs;
      if (notes && interestedFamily.student?.medical) notes += ' ' + interestedFamily.student.medical;

      studentDataToSave.allergies = interestedFamily.student?.allergies ?? '';
      studentDataToSave.medications = interestedFamily.student?.medications ?? '';
      studentDataToSave.doctorName = '';
      studentDataToSave.notes = notes;
      studentDataToSave.enrollment.interestedFamily = interestedFamily;
      studentDataToSave.marketplaceId = interestedFamily.student?.id ?? '';
      studentDataToSave.enrollment.scheduledDays = scheduledDays;
    }
    const docRef = await saveStudent(organization?.id, studentDataToSave, { merge: false });
    return { ...studentDataToSave, id: docRef.id };
  }, [
    studentForm,
    enrollmentForm,
    organization?.id,
    location?.id,
    saveContacts,
    interestedFamily,
    enrollmentStatus,
    enrollmentSource,
    scheduledDays,
  ]);

  const handleContinueToNextStep = useCallback(async () => {
    try {
      let isValid = false;
      scrollToTop();

      switch (currentStep) {
        case Step.STUDENT_INFO: {
          isValid = await studentForm.trigger();
          const isEnrollmentValid = await enrollmentForm.trigger(['room', 'startDate', 'scheduledDays']);
          if (isValid && isEnrollmentValid) {
            setCurrentStep(Step.DOCUMENTS);
          } else {
            showToast(ToastTypeEnum.Error, t('enrollments.pleaseCompleteRequiredFields'));
          }
          break;
        }
        case Step.DOCUMENTS:
          isValid = await enrollmentForm.trigger(['invitationRecipients', 'documents']);

          if (isValid) {
            if (organization.stripe?.accountId) {
              setCurrentStep(Step.INVOICE);
            } else {
              setCurrentStep(Step.SUMMARY);
            }
          } else {
            showToast(ToastTypeEnum.Error, t('enrollments.pleaseCompleteRequiredFields'));
          }
          break;
        case Step.INVOICE:
          isValid = await enrollmentForm.trigger([
            'parentEmail',
            'billingInterval',
            'billFor',
            'chargeDate',
            'tuition',
            'recurringFees',
            'oneTimeFees',
            'subsidized',
            'subsidyCopayment',
          ]);
          if (isValid) {
            setCurrentStep(Step.SUMMARY);
          } else {
            showToast(ToastTypeEnum.Error, t('enrollments.pleaseCompleteRequiredFields'));
          }
          break;
        case Step.SUMMARY:
          break;
        default:
          break;
      }
    } catch (error) {
      logError('Error while validating forms', error);
    }
  }, [scrollToTop, currentStep, enrollmentForm, studentForm, t, organization.stripe?.accountId]);

  const handleInvoiceReview = useCallback(async () => {
    // Same logic as continue step for invoice validation
    try {
      scrollToTop();
      const isValid = await enrollmentForm.trigger([
        'parentEmail',
        'billingInterval',
        'billFor',
        'chargeDate',
        'tuition',
        'recurringFees',
        'oneTimeFees',
        'subsidized',
        'subsidyCopayment',
      ]);
      if (isValid) {
        setCurrentStep(Step.SUMMARY);
      } else {
        showToast(ToastTypeEnum.Error, t('enrollments.pleaseCompleteRequiredFields'));
      }
    } catch (error) {
      logError('Error in handleInvoiceReview', error);
    }
  }, [enrollmentForm, scrollToTop, t]);

  const handleSetupInvoicesLater = useCallback(() => {
    // Clear all invoice-related data
    enrollmentForm.setValue('parentEmail', '');
    enrollmentForm.setValue('billingInterval', '');
    enrollmentForm.setValue('billFor', 'Next');
    enrollmentForm.setValue('chargeDate', '');
    enrollmentForm.setValue('tuition', 0);
    enrollmentForm.setValue('recurringFees', []);
    enrollmentForm.setValue('oneTimeFees', []);
    enrollmentForm.setValue('subsidized', false);
    enrollmentForm.setValue('subsidyCopayment', true);
    enrollmentForm.setValue('tuitionAndFees', {});

    // Go directly to summary
    setCurrentStep(Step.SUMMARY);
    scrollToTop();
  }, [enrollmentForm, scrollToTop]);

  const handleConfirm = useCallback(() => {
    gotoRouteByName(RouteNameEnum.STUDENTS);
  }, [gotoRouteByName]);

  const handleSaveAndAddAnother = useCallback(async () => {
    if (!isSaving) {
      setIsSaving(true);
      try {
        const savedStudent = await saveStudentLocal();
        if (!savedStudent) {
          showToast(ToastTypeEnum.Error, t('enrollments.failedToSaveStudent'));
          return;
        }

        studentForm.reset();
        enrollmentForm.reset();
        setInterestedFamily(undefined);
        setScheduledDays([]);
        setExtraNotes('');
        setContactsWithIds([]);
        setCurrentStep(Step.STUDENT_INFO);

        showToast(ToastTypeEnum.Success, t('enrollments.studentAddedNoStatus', { name: savedStudent.displayName }));
      } catch (error) {
        logError('Error during quick save', error);
      } finally {
        setIsSaving(false);
        scrollToTop(); // Jump to top to show the success toast
      }
    }
  }, [isSaving, saveStudentLocal, studentForm, enrollmentForm, t, scrollToTop]);

  const handleQuickSaveClick = useCallback(async () => {
    const isStudentValid = await studentForm.trigger();
    const isEnrollmentValid = await enrollmentForm.trigger(['room', 'startDate', 'scheduledDays']);

    if (!isStudentValid || !isEnrollmentValid) {
      showToast(ToastTypeEnum.Error, t('enrollments.pleaseCompleteRequiredFields'));
      scrollToTop();
      return;
    }

    // If validation passes, check if we should show dialog or proceed directly
    const dontAskAgain = localStorage.getItem('quickSaveDontAskAgain') === 'true';

    if (dontAskAgain) {
      handleSaveAndAddAnother();
      scrollToTop();
    } else {
      setShowQuickSaveDialog(true);
    }
  }, [studentForm, enrollmentForm, t, scrollToTop, handleSaveAndAddAnother]);

  const handleQuickSaveConfirm = useCallback(
    (dontAskAgain: boolean) => {
      // Validation is already done in handleQuickSaveClick, so we can proceed directly
      if (dontAskAgain) {
        localStorage.setItem('quickSaveDontAskAgain', 'true');
      }
      setShowQuickSaveDialog(false);
      handleSaveAndAddAnother();
    },
    [handleSaveAndAddAnother]
  );

  const handleRedirectToStudents = useCallback(() => {
    gotoRouteByName(RouteNameEnum.STUDENTS_FILTER, [{ name: 'filter', value: enrollmentStatus }]);
  }, [enrollmentStatus, gotoRouteByName]);

  if (!organization || !location) {
    return (
      <div className="rounded-lg bg-white p-4 shadow-md">
        <PageHeader pageName={t('students.addStudentPageTitle')} classes="add-student-page" />
        <h1 className="mb-4 text-2xl font-bold">{t('students.addStudentPageTitle')}</h1>
        <div className="flex items-center justify-center p-8">
          <div className="text-lg text-gray-600">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toast />
      <div className="rounded-lg bg-white p-4 shadow-md" ref={scrollContainerRef}>
        {currentStep === Step.STUDENT_INFO && (
          <StudentInformation
            studentForm={studentForm}
            enrollmentForm={enrollmentForm}
            marketplaceChildId={marketplaceChildId}
            setInterestedFamily={setInterestedFamily}
            desiredStartDate={desiredStartDate}
            defaultRoomId={defaultRoomId}
            scheduledDays={scheduledDays}
            onSaveSchedule={onSaveSchedule}
            parents={studentForm.watch('parents') ?? []}
          />
        )}
        {currentStep === Step.DOCUMENTS && (
          <StudentDocuments
            enrollmentForm={enrollmentForm}
            studentFromForm={studentFromForm}
            invitationRecipients={invitationRecipients}
            documents={enrollmentForm.watch('documents') || []}
          />
        )}
        {currentStep === Step.INVOICE && (
          <StudentInvoice
            enrollmentForm={enrollmentForm}
            studentFromForm={studentFromForm}
            invitationRecipients={invitationRecipients}
            scheduledDays={scheduledDays}
          />
        )}
        {currentStep === Step.SUMMARY && (
          <StudentSummary
            enrollmentForm={enrollmentForm}
            studentFromForm={studentFromForm}
            invitationRecipients={invitationRecipients}
            scheduledDays={scheduledDays}
            extraNotes={extraNotes}
            setExtraNotes={setExtraNotes}
          />
        )}
        <CommandButtons />
      </div>
      <CancelConfirmationDialog
        isOpen={showCancelDialog}
        onCancel={() => setShowCancelDialog(false)}
        onConfirm={handleConfirm}
      />
      <QuickSaveConfirmationModal
        isOpen={showQuickSaveDialog}
        onCancel={() => setShowQuickSaveDialog(false)}
        onConfirm={handleQuickSaveConfirm}
      />
      <NewEnrollmentInvitationConfirmationModal
        tuitionAndFees={enrollmentForm.getValues('tuitionAndFees')}
        documents={enrollmentForm.getValues('documents') || []}
        displayName={studentFromForm?.displayName || ''}
        enrollmentDate={enrollmentForm.getValues('startDate')}
        automaticallyEnroll={!!enrollmentForm.getValues('automaticallyEnroll')}
        enrollmentStatus={enrollmentStatus}
        invitationRecipients={invitationRecipients}
        isOpen={isModalOpen}
        onClose={handleRedirectToStudents}
        documentSharingFailed={documentShareFailed}
      />
      <NavigationPreventionModal {...modalProps} />
    </>
  );

  function CommandButtons() {
    const handlePrevious = () => {
      switch (currentStep) {
        case Step.STUDENT_INFO:
          setShowCancelDialog(true);
          break;
        case Step.DOCUMENTS:
          setCurrentStep(Step.STUDENT_INFO);
          scrollToTop();
          break;
        case Step.INVOICE:
          setCurrentStep(Step.DOCUMENTS);
          scrollToTop();
          break;
        case Step.SUMMARY:
          if (organization.stripe?.accountId) {
            setCurrentStep(Step.INVOICE);
          } else {
            setCurrentStep(Step.DOCUMENTS);
          }
          scrollToTop();
          break;
        default:
          break;
      }
    };

    const onSubmit = async () => {
      if (!isSaving) {
        setIsSaving(true);
        setDocumentShareFailed(false);
        try {
          const savedStudent = await saveStudentLocal();
          if (!savedStudent) {
            throw new Error('Failed to save student');
          }

          const enrollmentData = enrollmentForm.getValues();
          const documents = enrollmentData.documents || [];
          if (documents.length > 0) {
            await shareDocuments(savedStudent);
          }

          await sendInvoices(savedStudent);

          setIsModalOpen(true);
        } catch (error) {
          logError('Error during enrollment submission', error);
          // A failed invoice-plan save now rejects instead of resolving silently;
          // tell the provider instead of just stopping the spinner.
          showToast(
            ToastTypeEnum.Error,
            error instanceof Error && error.message === 'Failed to save student'
              ? t('enrollments.failedToSaveStudent')
              : t('enrollments.submissionFailed')
          );
          setIsSaving(false);
        }
      }
    };

    const isFirstStep = currentStep === Step.STUDENT_INFO;
    const isLastStep = currentStep === Step.SUMMARY;
    const isInvoiceStep = currentStep === Step.INVOICE;

    const parents = studentForm.watch('parents') ?? [];
    const hasAtLeastOneInvitation = parents.some(
      (parent) =>
        parent.shouldSendInvitation === true || (parent.contact && parent.contact.status === ContactStatus.verified)
    );
    const shouldDisableContinueButton = isFirstStep && !hasAtLeastOneInvitation;

    return (
      <div className="flex items-start justify-between" data-testid="as-form-btns">
        <Button
          primary={false}
          onClick={handlePrevious}
          data-testid="as-cancel-btn"
          preIcon={
            isFirstStep ? (
              <CloseIcon className="size-6" data-testid="as-cancel-icon" />
            ) : (
              <ArrowLeftIcon className="size-6" data-testid="as-previous-icon" />
            )
          }
        >
          {isFirstStep ? t('common.cancel') : t('common.previous')}
        </Button>
        <div className="flex items-center gap-4">
          {isFirstStep && (
            <Tooltip action="hover" content={t('enrollments.saveAndAddAnotherStudentTooltip')}>
              <InfoIcon className="size-6 text-gray-600" />
            </Tooltip>
          )}
          {isFirstStep && (
            <Button
              primary={false}
              loading={isSaving}
              onClick={handleQuickSaveClick}
              data-testid="as-save-and-add-btn"
              extraClasses="h-11"
            >
              {t('enrollments.saveAndAddAnotherStudent')}
            </Button>
          )}
          {isInvoiceStep && (
            <Button onClick={handleSetupInvoicesLater} data-testid="invoice-setup-later-btn" extraClasses="h-11">
              {t('enrollments.setUpInvoicesLater')}
            </Button>
          )}
          {isInvoiceStep && (
            <Button primary onClick={handleInvoiceReview} data-testid="invoice-review-btn" extraClasses="h-11">
              {t('enrollments.review')}
            </Button>
          )}
          {!isLastStep && !isInvoiceStep && (
            <Tooltip
              action={shouldDisableContinueButton ? 'hover' : 'none'}
              content={t('enrollments.disabledPackageButton')}
            >
              <Button
                primary
                onClick={handleContinueToNextStep}
                postIcon={<ArrowRightIcon className="size-6" data-testid="as-finish-icon" />}
                extraClasses="h-11"
                data-testid="as-finish-btn"
                disabled={shouldDisableContinueButton}
              >
                {isFirstStep ? t('enrollments.continueToEnrollmentPackage') : t('common.continue')}
              </Button>
            </Tooltip>
          )}
          {isLastStep && (
            <Button
              primary
              loading={isSaving}
              onClick={onSubmit}
              data-testid="es-send-invite"
              postIcon={<ArrowRightIcon className="size-6" />}
              extraClasses="h-11"
            >
              {t('enrollments.enrollmentInfoSendEnrollmentButton')}
            </Button>
          )}
        </div>
      </div>
    );
  }
};

export default AddStudentPage;
